import React, { useState, useEffect } from 'react';
import ProcessMap from './ProcessMap/ProcessMap';
import ProcessDetails from './ProcessDetails/ProcessDetails';
import Feedback from './Feedback/Feedback';
import Header from './Header/Header';
import { getService } from '@Client/runner.hooks/useService';
import {
  CustomTabs,
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import IProcessMap from '@Shared.Angular/@types/processMap';
import CommentTargetType from '@Shared.Angular/@types/core/contracts/constants/commentTargetType';
import TagList from '@Shared.Angular/flowingly.components/TagList/TagList';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { CustomTabPanel } from '@Shared.Angular/flowingly.components/CustomTabs/CustomTab/CustomTabPanel';
import IProcessMapNodeViewModel from '@Shared.Angular/@types/processMapNodeViewModel';

type Props = {
  processMapId: Guid;
  processMapHeight: number;
  onProcessMapRetrieved: (processMap: IProcessMap) => void;
  onSelectedNodeDataChange: (data: IProcessMapNodeViewModel) => void;
  hideFeedBack: boolean;
  isPublicMapView: boolean;
};

export const LeftPanel = (props: Props) => {
  const {
    processMapId,
    processMapHeight,
    onSelectedNodeDataChange,
    onProcessMapRetrieved,
    hideFeedBack,
    isPublicMapView
  } = props;
  const [selectedTabId, setSelectedTabId] = useState(TabId.PROCESS_MAP);
  const [processMap, setProcessMap] = useState<IProcessMap>({} as IProcessMap);
  const [commentTargetType, setCommentTargetType] = useState(null);
  const [commentLength, setCommentLength] = useState(0);
  const [editableByCurrentUser, setEditableByCurrentUser] = useState(false);
  const appConfig = getService('APP_CONFIG');

  useEffect(() => {
    // set ProcessMap, the default tab when switching between components using breadcrumbs
    setSelectedTab(TabId.PROCESS_MAP);
  }, [processMapId]);

  const shouldFeedbackBeVisible = (editableByCurrentUser: boolean) => {
    return (
      appConfig.flowModelFeedbackVisibility.toLowerCase() === 'all' ||
      (appConfig.flowModelFeedbackVisibility.toLowerCase() === 'flowowners' &&
        editableByCurrentUser)
    );
  };

  const setSelectedTab = (newTabId: TabId) => {
    setSelectedTabId(newTabId);
    onSelectedNodeDataChange(null);
  };

  const handleProcessMapLoad = (data) => {
    setProcessMap(data);
    if (shouldFeedbackBeVisible(data.editableByCurrentUser)) {
      setCommentTargetType(CommentTargetType.FLOW_MODEL);
    }
    setEditableByCurrentUser(data.editableByCurrentUser);
  };

  const handleCommentsUpdate = (totalComments: number) => {
    setCommentLength(totalComments);
  };

  const tabs: ICustomTab[] = [
    { id: TabId.PROCESS_MAP, label: 'Process Map' },
    { id: TabId.PROCESS_DETAILS, label: 'Process Details' },
    {
      id: TabId.FLOW_MODEL_TAGS,
      label: 'Flow Model Tags',
      isHidden: !appConfig.enableTagging
    },
    {
      id: TabId.FEEDBACK,
      label: `${
        commentLength > 0 ? `Feedback (${commentLength})` : 'Feedback'
      }`,
      isDisabled: !shouldFeedbackBeVisible(editableByCurrentUser),
      isHidden: hideFeedBack
    }
  ];

  const visibleTabs = tabs.filter((tab) => !tab.isHidden);

  const tagGroupsFormatted = (tagGroups: ITagGroupDetail[]) => {
    const result: Record<string, { tagNames: string[]; color: string }> = {};
    tagGroups.forEach((group) => {
      result[group.name] = {
        tagNames: group.tags.map((tag) => tag.name),
        color: group.color
      };
    });
    return result;
  };

  return (
    <div id="process-map-leftpanel">
      <Header
        processOwner={processMap.processOwner}
        processDescription={processMap.description}
      />
      <CustomTabs
        selectedTabId={selectedTabId}
        onChange={setSelectedTab}
        tabItems={visibleTabs}
      />
      <CustomTabPanel
        isShown={selectedTabId === TabId.PROCESS_MAP}
        tabId={TabId.PROCESS_MAP}
      >
        <ProcessMap
          isPublicMapView={isPublicMapView}
          processMapHeight={processMapHeight}
          isVisible={selectedTabId === TabId.PROCESS_MAP}
          processMapId={processMapId}
          onProcessMapRetrieved={onProcessMapRetrieved}
          onSelectedNodeDataChange={onSelectedNodeDataChange}
          onProcessMapLoad={handleProcessMapLoad}
        />
      </CustomTabPanel>
      <CustomTabPanel
        isShown={selectedTabId === TabId.PROCESS_DETAILS}
        tabId={TabId.PROCESS_DETAILS}
      >
        <ProcessDetails isPublicMapView={isPublicMapView} {...processMap} />
      </CustomTabPanel>

      <CustomTabPanel
        isShown={selectedTabId === TabId.FLOW_MODEL_TAGS}
        tabId={TabId.FLOW_MODEL_TAGS}
      >
        <div className="process-map-tags-container">
          <TagList
            showBorder={false}
            tagsByGroupName={tagGroupsFormatted(processMap.tags || [])}
          />
        </div>
      </CustomTabPanel>

      <CustomTabPanel
        isShown={selectedTabId === TabId.FEEDBACK}
        tabId={TabId.FEEDBACK}
      >
        <Feedback
          commentAdded
          commentTargetId={processMapId}
          commentTargetType={commentTargetType}
          onCommentsUpdate={handleCommentsUpdate}
        ></Feedback>
      </CustomTabPanel>
    </div>
  );
};

enum TabId {
  PROCESS_MAP = 'processmap',
  PROCESS_DETAILS = 'processdetails',
  FLOW_MODEL_TAGS = 'flowmodeltags',
  FEEDBACK = 'feedback'
}
